import { detectOverflow, type Modifier, type Options, type Placement } from '@popperjs/core';

const MIN_HEIGHT = 100;
const VIEWPORT_GAP = 16;

type SameWidthOptions = Record<string, never>;

const sameWidth: Modifier<'sameWidth', SameWidthOptions> = {
  name: 'sameWidth',
  enabled: true,
  phase: 'beforeWrite',
  requires: ['computeStyles'],
  fn: ({ state }) => {
    state.styles.popper.width = `${state.rects.reference.width}px`;
  },
  effect: ({ state }) => {
    const { reference } = state.elements;
    if (reference instanceof HTMLElement) {
      state.elements.popper.style.width = `${reference.offsetWidth}px`;
    }
  },
};

type MaxHeightOptions = {
  padding: number;
};

const maxHeight: Modifier<'dropdownMaxHeight', MaxHeightOptions> = {
  name: 'dropdownMaxHeight',
  enabled: true,
  phase: 'main',
  requiresIfExists: ['offset', 'preventOverflow', 'flip'],
  fn: ({ state, name, options }) => {
    const overflow = detectOverflow(state, { padding: options.padding, rootBoundary: 'viewport' });
    const verticalShift = state.modifiersData.preventOverflow?.y ?? 0;
    const isFlippedUp = state.placement.split('-')[0] === 'top';
    const overflowingSide = isFlippedUp ? overflow.top : overflow.bottom;
    const availableHeight = state.rects.popper.height - overflowingSide - verticalShift;
    state.modifiersData[name] = { height: Math.max(MIN_HEIGHT, availableHeight) };
  },
};

type DropdownPlacement = Extract<Placement, 'top-start' | 'bottom-start'>;

const FALLBACK_PLACEMENT: Record<DropdownPlacement, DropdownPlacement> = {
  'top-start': 'bottom-start',
  'bottom-start': 'top-start',
};

export function getDropdownPopperOptions(
  placement: DropdownPlacement,
): Omit<Partial<Options>, 'modifiers'> & { modifiers: Options['modifiers'] } {
  const fallbackPlacement = FALLBACK_PLACEMENT[placement];
  const padding = VIEWPORT_GAP;

  const modifiers: Options['modifiers'] = [
    { name: 'eventListeners', options: { scroll: true, resize: true } },
    { name: 'flip', options: { fallbackPlacements: [fallbackPlacement], rootBoundary: 'viewport', padding } },
    { name: 'preventOverflow', options: { mainAxis: true, altAxis: false, rootBoundary: 'viewport', padding } },
    sameWidth,
    { ...maxHeight, options: { padding } },
  ];

  return { placement, strategy: 'fixed', modifiers };
}
